import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { currentParams, readValues, writeValues } from '../utils/urlParams';

/**
 * #120936 — Đồng bộ bộ lọc của 1 trang với tham số trên URL (deep-link).
 * Bản cho `manage-coffe-admin` (CRA + react-router-dom v6). Bản Next.js của
 * `on-coffee-staff` phải giữ ĐÚNG hợp đồng này — xem KHUON-CHUAN-DEEPLINK.md mục 1c.
 *
 *   const [f, setF, resetF, { ignored }] = useFilterParams({
 *     status: { default: 'ACTIVE', valid: v => STATUSES.includes(v.toUpperCase()),
 *               parse: v => v.toUpperCase() },
 *     search: { default: '', debounce: 350 },
 *     page:   { default: 1, parse: Number, valid: v => /^\d+$/.test(v) && Number(v) >= 1 },
 *   });
 *
 * 7 luật bắt buộc (mục 1c của khuôn) — phần chống lỗi thật, không phải mấy dòng mã:
 *  1. MỘT CHIỀU. URL→state đúng 1 lần lúc mount (lazy init). state→URL chỉ khi NGƯỜI DÙNG
 *     thao tác. CẤM cặp useEffect hai chiều — đó là vòng lặp render.
 *  2. `replace`, không `push` — nút Back phải rời trang, không hoàn tác từng ký tự.
 *  3. Chỉ ghi param KHÁC mặc định (thực thi trong `writeValues`).
 *  4. Đổi bất kỳ bộ lọc nào → `page` về 1.
 *  5. Ô tìm kiếm debounce trước khi ghi URL (`debounce: 350`).
 *  6. F5 phải ra đúng màn hình cũ — hệ quả của luật 1 + 3.
 *  7. (chỉ Next.js) `<Suspense>` — không áp dụng cho kho CRA này.
 */
export default function useFilterParams(spec) {
  // Giữ nguyên spec của lần render đầu: spec hay được khai báo inline nên đổi tham chiếu
  // mỗi render; bám theo nó sẽ làm mọi useCallback/useEffect dưới đây chạy lại vô ích.
  const specRef = useRef(spec);
  const [, setSearchParams] = useSearchParams();

  // LUẬT 1 — URL → state đúng MỘT lần, lúc mount.
  const initial = useMemo(() => readValues(currentParams(), specRef.current), []);
  const [values, setValues] = useState(initial.values);
  const [ignored, setIgnored] = useState(initial.ignored);

  const debounceTimers = useRef({});
  useEffect(
    () => () => Object.values(debounceTimers.current).forEach(clearTimeout),
    [],
  );

  // #021121 — bản ghi giá trị lọc MỚI NHẤT, để `update`/`reset` tính `merged` ở NGOÀI hàm
  // updater của `setValues`. Trước đây `pushToUrl` được gọi ngay trong updater — updater chạy
  // trong pha render, nên `setSearchParams` (state của React Router) bị cập nhật lúc đang render
  // component gọi `update` → warning React "Cannot update a component (`RouterProvider`) while
  // rendering a different component (`UserListPage`)". `values` CHỈ đổi qua chính `update`/`reset`
  // nên ref này luôn khớp giá trị logic mới nhất, kể cả khi nhiều lần cập nhật dồn trong 1 handler.
  const valuesRef = useRef(values);

  // LUẬT 2 — ghi URL bằng replace. Dựng từ URL THẬT đang hiển thị (không phải từ
  // `searchParams` đã chụp lúc render) để không xoá mất param do nơi khác vừa ghi,
  // điển hình là `branch_id` của BranchContext.
  const pushToUrl = useCallback(
    (nextValues) => {
      const next = writeValues(currentParams(), specRef.current, nextValues);
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  /**
   * Cập nhật bộ lọc do NGƯỜI DÙNG thao tác. Nhận object vá hoặc hàm (prev) => vá.
   * Param khai báo `debounce` chỉ hoãn phần GHI URL — state đổi ngay để ô nhập không giật.
   */
  const update = useCallback((patch) => {
    const prev = valuesRef.current;
    const delta = typeof patch === 'function' ? patch(prev) : patch;
    const merged = { ...prev, ...delta };

    // LUẬT 4 — đổi bộ lọc thì về trang 1 (trừ khi chính `page` đang được đổi).
    const touched = Object.keys(delta);
    if ('page' in specRef.current && !touched.includes('page') && touched.length > 0) {
      merged.page = typeof specRef.current.page.default === 'function'
        ? specRef.current.page.default()
        : specRef.current.page.default;
    }

    // Cập nhật state TRƯỚC, rồi mới ghi URL — KHÔNG gọi `pushToUrl` bên trong callback của
    // `setValues` (xem #021121 ở khai báo `valuesRef`). Ghi ref ngay để lần `update` kế tiếp
    // trong cùng 1 handler thấy được giá trị vừa vá.
    valuesRef.current = merged;
    setValues(merged);

    // LUẬT 5 — hoãn ghi URL nếu param vừa đổi có `debounce`.
    const delay = touched.reduce(
      (max, name) => Math.max(max, specRef.current[name]?.debounce || 0),
      0,
    );
    const key = delay > 0 ? touched.join('|') : '__now__';
    clearTimeout(debounceTimers.current[key]);
    if (delay > 0) {
      debounceTimers.current[key] = setTimeout(() => pushToUrl(merged), delay);
    } else {
      pushToUrl(merged);
    }
  }, [pushToUrl]);

  /** Về mặc định toàn bộ → URL sạch hẳn các param của spec này. */
  const reset = useCallback(() => {
    const defaults = readValues(new URLSearchParams(), specRef.current).values;
    valuesRef.current = defaults;
    setValues(defaults);
    setIgnored([]);
    pushToUrl(defaults);
  }, [pushToUrl]);

  /** LUẬT 3b — người dùng phải THẤY được là tham số đã bị bỏ qua. */
  const dismissIgnored = useCallback(() => setIgnored([]), []);

  const meta = useMemo(() => ({ ignored, dismissIgnored }), [ignored, dismissIgnored]);

  return [values, update, reset, meta];
}
